import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';

const STATIONS = ['kitchen', 'bar', 'pastry'];

// ─── Unit conversion (mirrors backend unitConversionService) ─────────────────
const VOLUME = { ml:1,mL:1,cl:10,dl:100,l:1000,L:1000,tsp:4.92892,tbsp:14.7868,cup:236.588,'fl oz':29.5735,pt:473.176,qt:946.353,gal:3785.41 };
const WEIGHT = { mg:0.001,g:1,kg:1000,oz:28.3495,lb:453.592,lbs:453.592 };
const COUNT  = new Set(['pcs','pc','piece','pieces','pack','packs','box','boxes','bottle','bottles','can','cans','slice','slices','serving','servings','stick','sticks','bunch','bunches','head','heads','clove','cloves','bag','bags','sachet','sachets','sheet','sheets']);

function unitCategory(u) {
  if (!u) return null;
  if (VOLUME[u] !== undefined) return 'volume';
  if (WEIGHT[u] !== undefined) return 'weight';
  if (COUNT.has(u)) return 'count';
  return null;
}

function convertUnit(qty, from, to) {
  if (!from || !to || from === to) return null; // null means no conversion needed/possible
  const fc = unitCategory(from), tc = unitCategory(to);
  if (!fc || !tc || fc !== tc || fc === 'count') return null;
  const base = fc === 'volume' ? VOLUME : WEIGHT;
  return (qty * base[from]) / base[to];
}

function conversionHint(qty, fromUnit, toUnit) {
  const result = convertUnit(parseFloat(qty) || 0, fromUnit, toUnit);
  if (result === null) return null;
  const r = Math.round(result * 10000) / 10000;
  return `= ${r} ${toUnit}`;
}

const EMPTY_MENU_FORM = {
  isIngredient: false,
  name: '', category: '', price: '', grabPrice: '', foodCost: '',
  printStation: 'kitchen', isAvailable: true,
  stockUnit: '', stockQuantity: '', reorderPoint: '', unitCost: '',
  ingredients: [],
};

const EMPTY_ING = { productId: null, name: '', quantity: '', unit: 'pcs' };

const FALLBACK_UOMS = ['pcs', 'kg', 'g', 'L', 'mL', 'pack', 'box', 'bottle', 'can', 'slice', 'cup', 'tbsp', 'tsp', 'serving'];

function peso(centavos) {
  return (centavos / 100).toFixed(2);
}

function useUoms() {
  const [uoms, setUoms] = useState(FALLBACK_UOMS);
  useEffect(() => {
    api.get('/billing/unit-of-measure')
      .then((res) => {
        const list = (res.data.data || []).map((u) => u.Abbreviation || u.UnitOfMeasureName).filter(Boolean);
        if (list.length > 0) setUoms(list);
      })
      .catch(() => {});
  }, []);
  return uoms;
}

function usePosCategories() {
  const { getAccessToken } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const token = getAccessToken();
      const res = await api.get('/restaurant/pos-reports/menu-categories', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories((res.data.categories || []).map((c) => c.name));
    } catch {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => { load(); }, [load]);

  return { categories, loading, reload: load };
}

// ─── Product combobox (links ingredient name to a PosProduct) ─────────────────

function ProductCombobox({ value, productId, allProducts, excludeId, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const ref = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    if (!open) return;
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Prioritise raw ingredients in the dropdown — they're the ones you'd normally link
  const filtered = allProducts
    .filter((p) => p._id !== excludeId && p.name.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => (b.isIngredient ? 1 : 0) - (a.isIngredient ? 1 : 0))
    .slice(0, 10);

  function select(p) {
    onChange({ productId: p._id, name: p.name, unit: p.stockUnit || '' });
    setQuery(p.name);
    setOpen(false);
  }

  function handleInput(e) {
    setQuery(e.target.value);
    onChange({ productId: null, name: e.target.value, unit: undefined });
    setOpen(true);
  }

  return (
    <div ref={ref} className="relative flex-1">
      <div className="relative">
        <input
          className={`w-full border rounded px-2 py-1 text-sm pr-6 ${
            productId ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
          }`}
          placeholder="Product or raw ingredient"
          value={query}
          onChange={handleInput}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {productId && (
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-blue-400 text-xs" title="Linked to product">⬡</span>
        )}
      </div>
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto text-sm">
          {filtered.map((p) => (
            <li key={p._id} onMouseDown={() => select(p)}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex items-center justify-between gap-2">
              <span className="font-medium text-gray-800">{p.name}</span>
              <span className="flex items-center gap-1 shrink-0">
                {p.isIngredient && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">ingredient</span>
                )}
                {p.isIngredient && p.stockUnit && (
                  <span className="text-xs text-gray-400">{p.stockUnit}</span>
                )}
                {!p.isIngredient && (
                  <span className="text-xs text-gray-400">{p.category}</span>
                )}
              </span>
            </li>
          ))}
          {query && !filtered.some((p) => p.name.toLowerCase() === query.toLowerCase()) && (
            <li className="px-3 py-2 text-gray-400 text-xs border-t">
              "{query}" — free-text (not linked)
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// ─── Ingredients editor ───────────────────────────────────────────────────────

function IngredientsEditor({ ingredients, onChange, allProducts, excludeId, uoms }) {
  function update(idx, patch) {
    onChange(ingredients.map((ing, i) => i === idx ? { ...ing, ...patch } : ing));
  }
  function add()        { onChange([...ingredients, { ...EMPTY_ING }]); }
  function remove(idx)  { onChange(ingredients.filter((_, i) => i !== idx)); }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          Ingredients (recipe)
        </label>
        <button type="button" onClick={add} className="text-xs text-blue-600 hover:underline">
          + Add
        </button>
      </div>
      {ingredients.length === 0 ? (
        <p className="text-xs text-gray-400 italic">
          No ingredients — simple product. Add ingredients to make it a compound/recipe item.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-[1fr_68px_72px_56px_20px] gap-x-2 mb-1 px-0.5">
            <span className="text-xs text-gray-400">Product / ingredient</span>
            <span className="text-xs text-gray-400">Qty</span>
            <span className="text-xs text-gray-400">Unit</span>
            <span className="text-xs text-gray-400 text-right">Cost</span>
            <span />
          </div>
          <div className="space-y-2">
            {ingredients.map((ing, i) => {
              const linkedProd = ing.productId
                ? allProducts.find((p) => p._id === ing.productId)
                : null;
              const stockUnit = linkedProd?.stockUnit;
              const hint = stockUnit && ing.unit && ing.unit !== stockUnit && ing.quantity
                ? conversionHint(ing.quantity, ing.unit, stockUnit)
                : null;
              const incompatible = stockUnit && ing.unit && ing.unit !== stockUnit && hint === null;

              // Per-ingredient cost — raw ingredients use unitCost, menu items use foodCost
              let ingCost = null;
              if (linkedProd && ing.quantity) {
                const qty = parseFloat(ing.quantity) || 0;
                if (linkedProd.isIngredient && linkedProd.unitCost > 0 && qty > 0) {
                  let effectiveQty = qty;
                  if (ing.unit !== stockUnit) {
                    const cv = convertUnit(qty, ing.unit, stockUnit);
                    effectiveQty = cv !== null ? cv : null;
                  }
                  if (effectiveQty !== null) ingCost = effectiveQty * linkedProd.unitCost;
                } else if (!linkedProd.isIngredient && linkedProd.foodCost > 0 && qty > 0) {
                  ingCost = qty * linkedProd.foodCost;
                }
              }

              return (
                <div key={i}>
                  <div className="grid grid-cols-[1fr_68px_72px_56px_20px] gap-x-2 items-center">
                    <ProductCombobox
                      value={ing.name}
                      productId={ing.productId}
                      allProducts={allProducts}
                      excludeId={excludeId}
                      onChange={(patch) => {
                        const next = { ...ing, ...patch };
                        if (patch.unit !== undefined && patch.unit) next.unit = patch.unit;
                        update(i, next);
                      }}
                    />
                    <input
                      className="border border-gray-200 rounded px-2 py-1 text-sm"
                      type="number" min="0" step="any" placeholder="Qty"
                      value={ing.quantity}
                      onChange={(e) => update(i, { quantity: e.target.value })}
                    />
                    <select
                      className={`border rounded px-2 py-1 text-sm ${
                        incompatible ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                      value={ing.unit}
                      onChange={(e) => update(i, { unit: e.target.value })}
                    >
                      {uoms.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <span className="text-right text-xs text-gray-500">
                      {ingCost !== null ? `₱${peso(ingCost)}` : '—'}
                    </span>
                    <button type="button" onClick={() => remove(i)}
                      className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                  </div>
                  {hint && (
                    <p className="text-xs text-blue-500 ml-1 mt-0.5">
                      ↳ auto-converts to {hint} (stock unit: {stockUnit})
                    </p>
                  )}
                  {incompatible && (
                    <p className="text-xs text-red-500 ml-1 mt-0.5">
                      ⚠ Cannot convert {ing.unit} → {stockUnit} — deduction will be skipped
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            ⬡ blue = linked to a product · recipe unit auto-converts to ingredient's stock unit on sale
          </p>
        </>
      )}
    </div>
  );
}

// ─── Product modal ─────────────────────────────────────────────────────────────

function ProductModal({ product, allProducts, categories, onClose, onSaved }) {
  const { getAccessToken } = useAuth();
  const uoms = useUoms();
  const isEdit = !!product?._id;

  // Merge PosCategory names + categories already used on existing products
  const categoryOptions = useMemo(() => {
    const fromProducts = [...new Set(allProducts.map((p) => p.category).filter(Boolean))];
    return [...new Set([...categories, ...fromProducts])].sort();
  }, [categories, allProducts]);

  const [addingNewCategory, setAddingNewCategory] = useState(
    // If editing a product whose category isn't in the merged list, start in "new" mode
    isEdit && product?.category && !categories.includes(product.category) ? true : false
  );

  const [form, setForm] = useState(() => {
    if (!isEdit) return { ...EMPTY_MENU_FORM };
    return {
      isIngredient:  product.isIngredient || false,
      name:          product.name,
      category:      product.category || '',
      price:         product.isIngredient ? '' : peso(product.price),
      grabPrice:     product.grabPrice ? peso(product.grabPrice) : '',
      foodCost:      product.foodCost  ? peso(product.foodCost)  : '',
      printStation:  product.printStation || 'kitchen',
      isAvailable:   product.isAvailable !== false,
      stockUnit:     product.stockUnit || '',
      stockQuantity: product.stockQuantity != null ? String(product.stockQuantity) : '',
      reorderPoint:  product.reorderPoint != null ? String(product.reorderPoint) : '',
      unitCost:      product.unitCost ? peso(product.unitCost) : '',
      ingredients:   (product.ingredients || []).map((ing) => ({
        productId: ing.productId || null,
        name:      ing.name,
        quantity:  ing.quantity != null ? String(ing.quantity) : '',
        unit:      ing.unit || 'pcs',
      })),
    };
  });

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  function set(patch) { setForm((f) => ({ ...f, ...patch })); }

  // Auto-compute food cost from linked ingredients (centavos).
  // Raw ingredients use unitCost × converted qty.
  // Menu items (e.g. leche flan in halo-halo) use foodCost × qty — no unit conversion.
  const recipeCost = useMemo(() => {
    if (form.isIngredient || form.ingredients.length === 0) return null;
    let total = 0;
    for (const ing of form.ingredients) {
      if (!ing.productId || !ing.quantity) return null;
      const prod = allProducts.find((p) => p._id === ing.productId);
      if (!prod) return null;
      const qty = parseFloat(ing.quantity);
      if (!qty) return null;

      if (prod.isIngredient) {
        // Raw ingredient: cost = converted qty × unitCost (centavos/stockUnit)
        if (!(prod.unitCost > 0) || !prod.stockUnit) return null;
        let effectiveQty = qty;
        if (ing.unit !== prod.stockUnit) {
          const cv = convertUnit(qty, ing.unit, prod.stockUnit);
          if (cv === null) return null;
          effectiveQty = cv;
        }
        total += effectiveQty * prod.unitCost;
      } else {
        // Menu item used as sub-component: cost = qty × foodCost (centavos/serving)
        if (!(prod.foodCost > 0)) return null;
        total += qty * prod.foodCost;
      }
    }
    return Math.round(total); // centavos
  }, [form.ingredients, form.isIngredient, allProducts]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(null);
    if (!form.name.trim()) { setErr('Name is required'); return; }
    if (!form.isIngredient && (!form.price || isNaN(parseFloat(form.price)))) {
      setErr('Valid price required for menu items'); return;
    }
    if (form.isIngredient && !form.stockUnit) {
      setErr('Stock unit is required for raw ingredients'); return;
    }

    setSaving(true);
    try {
      const token = getAccessToken();
      const payload = {
        isIngredient:  form.isIngredient,
        name:          form.name.trim(),
        category:      form.category.trim() || 'Uncategorized',
        price:         form.isIngredient ? 0 : parseFloat(form.price),
        grabPrice:     (!form.isIngredient && form.grabPrice) ? parseFloat(form.grabPrice) : null,
        foodCost:      (!form.isIngredient && form.foodCost)  ? parseFloat(form.foodCost)  : 0,
        printStation:  form.isIngredient ? 'kitchen' : form.printStation,
        isAvailable:   form.isAvailable,
        stockUnit:     form.isIngredient ? form.stockUnit : null,
        stockQuantity: form.isIngredient ? (parseFloat(form.stockQuantity) || 0) : 0,
        reorderPoint:  form.isIngredient && form.reorderPoint ? parseFloat(form.reorderPoint) : null,
        unitCost:      form.isIngredient && form.unitCost ? parseFloat(form.unitCost) : 0,
        ingredients:   form.ingredients
          .filter((ing) => ing.name.trim())
          .map((ing) => ({
            productId: ing.productId || null,
            name:      ing.name.trim(),
            quantity:  parseFloat(ing.quantity) || 0,
            unit:      ing.unit || 'pcs',
          })),
      };

      const headers = { Authorization: `Bearer ${token}` };
      if (isEdit) {
        await api.put(`/restaurant/pos-reports/menu/${product._id}`, payload, { headers });
      } else {
        await api.post('/restaurant/pos-reports/menu', payload, { headers });
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-gray-800">
            {isEdit ? 'Edit Product' : 'Add Product'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {err && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{err}</p>}

          {/* Type toggle */}
          <div className="flex gap-2">
            <button type="button"
              onClick={() => set({ isIngredient: false })}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                !form.isIngredient
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}>
              Menu Item
            </button>
            <button type="button"
              onClick={() => set({ isIngredient: true })}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                form.isIngredient
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}>
              Raw Ingredient
            </button>
          </div>

          {form.isIngredient && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">
              Raw ingredients are tracked in inventory and will not appear on the POS order screen.
            </p>
          )}

          {/* Name + category */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              {form.isIngredient ? 'Ingredient Name' : 'Product Name'} *
            </label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={form.name} onChange={(e) => set({ name: e.target.value })}
              placeholder={form.isIngredient ? 'e.g. Pork Belly' : 'e.g. Sisig'} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Category</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={addingNewCategory ? '__new__' : (form.category || '')}
              onChange={(e) => {
                if (e.target.value === '__new__') {
                  setAddingNewCategory(true);
                  set({ category: '' });
                } else {
                  setAddingNewCategory(false);
                  set({ category: e.target.value });
                }
              }}
            >
              <option value="">— select category —</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
              <option value="__new__">+ Add new category…</option>
            </select>
            {addingNewCategory && (
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-1"
                autoFocus
                placeholder={form.isIngredient ? 'e.g. Meat, Produce, Dairy' : 'e.g. Mains'}
                value={form.category}
                onChange={(e) => set({ category: e.target.value })}
              />
            )}
          </div>

          {/* Menu item fields */}
          {!form.isIngredient && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Dine-in Price (₱) *
                  </label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    type="number" min="0" step="0.01"
                    value={form.price} onChange={(e) => set({ price: e.target.value })}
                    placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Food Cost (₱)
                  </label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    type="number" min="0" step="0.01"
                    value={form.foodCost} onChange={(e) => set({ foodCost: e.target.value })}
                    placeholder="0.00" />
                  {recipeCost !== null ? (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-xs text-blue-600">Recipe: ₱{peso(recipeCost)}</span>
                      <button type="button"
                        onClick={() => set({ foodCost: peso(recipeCost) })}
                        className="text-xs text-blue-600 underline hover:text-blue-800">
                        Apply
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">For P-Mix report</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Grab Price (₱)
                  </label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    type="number" min="0" step="0.01"
                    value={form.grabPrice} onChange={(e) => set({ grabPrice: e.target.value })}
                    placeholder="Optional" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Print Station
                  </label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={form.printStation} onChange={(e) => set({ printStation: e.target.value })}>
                    {STATIONS.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.isAvailable}
                      onChange={(e) => set({ isAvailable: e.target.checked })}
                      className="h-4 w-4 accent-blue-600" />
                    <span className="text-sm text-gray-700">Available on POS</span>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Raw ingredient fields */}
          {form.isIngredient && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Stock Unit *
                  </label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={form.stockUnit} onChange={(e) => set({ stockUnit: e.target.value })}>
                    <option value="">— select —</option>
                    {uoms.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Unit for stock-in</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Unit Cost (₱ per {form.stockUnit || 'unit'})
                  </label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    type="number" min="0" step="0.0001"
                    value={form.unitCost} onChange={(e) => set({ unitCost: e.target.value })}
                    placeholder="0.00" />
                  <p className="text-xs text-gray-400 mt-1">Used for food cost compute</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Current Stock
                  </label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    type="number" min="0" step="any"
                    value={form.stockQuantity} onChange={(e) => set({ stockQuantity: e.target.value })}
                    placeholder="0" />
                  <p className="text-xs text-gray-400 mt-1">{form.stockUnit || '—'}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Reorder Point
                  </label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    type="number" min="0" step="any"
                    value={form.reorderPoint} onChange={(e) => set({ reorderPoint: e.target.value })}
                    placeholder="Optional" />
                  <p className="text-xs text-gray-400 mt-1">{form.stockUnit || '—'}</p>
                </div>
              </div>
            </>
          )}

          <hr />

          <IngredientsEditor
            ingredients={form.ingredients}
            allProducts={allProducts}
            excludeId={product?._id}
            uoms={uoms}
            onChange={(ingredients) => set({ ingredients })}
          />

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'all',        label: 'All' },
  { key: 'menu',       label: 'Menu Items' },
  { key: 'ingredient', label: 'Raw Ingredients' },
];

export default function RestaurantMenuPage() {
  const { getAccessToken } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [deleting, setDeleting] = useState(null);

  const { categories } = usePosCategories();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const res = await api.get('/restaurant/pos-reports/menu', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data.products || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(product) {
    if (!window.confirm(`Remove "${product.name}" from the menu?`)) return;
    setDeleting(product._id);
    try {
      const token = getAccessToken();
      await api.delete(`/restaurant/pos-reports/menu/${product._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts((prev) => prev.filter((p) => p._id !== product._id));
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    } finally {
      setDeleting(null);
    }
  }

  const filtered = products.filter((p) => {
    const matchesTab = tab === 'all' || (tab === 'ingredient' ? p.isIngredient : !p.isIngredient);
    const matchesSearch = !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.category?.toLowerCase().includes(search.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const grouped = filtered.reduce((acc, p) => {
    const cat = p.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const counts = {
    all:        products.length,
    menu:       products.filter((p) => !p.isIngredient).length,
    ingredient: products.filter((p) => p.isIngredient).length,
  };

  return (
    <Layout pageTitle="Menu Management">
      <div className="p-6 bg-gray-50 min-h-screen">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Menu & Ingredients</h1>
            <p className="text-sm text-gray-500">Manage POS menu items and raw ingredient inventory</p>
          </div>
          <button onClick={() => setModal('add')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
            + Add Product
          </button>
        </div>

        {/* Tabs + search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 self-start">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  tab === t.key ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-700'
                }`}>
                {t.label}
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  tab === t.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-400'
                }`}>{counts[t.key]}</span>
              </button>
            ))}
          </div>
          <input
            className="sm:w-64 border border-gray-200 rounded-lg px-3 py-2 text-sm"
            placeholder="Search name or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <p className="text-gray-400 text-sm">No products yet. Add your first menu item or raw ingredient.</p>
          </div>
        ) : Object.keys(grouped).length === 0 ? (
          <p className="text-sm text-gray-400 text-center mt-10">No results for "{search}"</p>
        ) : (
          Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
            <div key={category} className="mb-6">
              <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">{category}</h2>
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-400 uppercase border-b">
                      <th className="text-left px-4 py-3 font-medium">Name</th>
                      <th className="text-center px-4 py-3 font-medium">Type</th>
                      <th className="text-center px-4 py-3 font-medium">Station / Unit</th>
                      <th className="text-right px-4 py-3 font-medium">Price / Stock</th>
                      <th className="text-right px-4 py-3 font-medium">Food Cost</th>
                      <th className="text-right px-4 py-3 font-medium">Margin</th>
                      <th className="text-right px-4 py-3 font-medium">Grab Price</th>
                      <th className="text-center px-4 py-3 font-medium">Status</th>
                      <th className="text-center px-4 py-3 font-medium">Recipe</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p._id} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-800">{p.name}</td>
                        <td className="px-4 py-3 text-center">
                          {p.isIngredient ? (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                              ingredient
                            </span>
                          ) : (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                              menu
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {p.isIngredient ? (
                            <span className="text-xs text-gray-600 font-medium">
                              {p.stockUnit || '—'}
                            </span>
                          ) : (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              p.printStation === 'kitchen' ? 'bg-orange-100 text-orange-700' :
                              p.printStation === 'bar'     ? 'bg-purple-100 text-purple-700' :
                                                             'bg-pink-100 text-pink-700'
                            }`}>{p.printStation}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">
                          {p.isIngredient ? (
                            <span>
                              {p.stockQuantity ?? 0}
                              <span className="text-gray-400 ml-1 text-xs">{p.stockUnit}</span>
                              {p.reorderPoint != null && p.stockQuantity <= p.reorderPoint && (
                                <span className="ml-1 text-xs text-red-500" title="Low stock">⚠</span>
                              )}
                            </span>
                          ) : (
                            `₱${peso(p.price)}`
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!p.isIngredient && p.foodCost > 0
                            ? <span className="text-gray-700">₱{peso(p.foodCost)}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!p.isIngredient && p.price > 0 && p.foodCost > 0 ? (() => {
                            const pct = ((p.price - p.foodCost) / p.price) * 100;
                            const pos = pct >= 0;
                            return (
                              <span className={`text-xs font-semibold ${pos ? 'text-emerald-600' : 'text-red-500'}`}>
                                {pos ? '+' : ''}{pct.toFixed(1)}%
                              </span>
                            );
                          })() : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!p.isIngredient && p.grabPrice
                            ? <span className="text-gray-700">₱{peso(p.grabPrice)}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {p.isIngredient ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <span className={`inline-block w-2 h-2 rounded-full ${p.isAvailable ? 'bg-green-400' : 'bg-gray-300'}`} />
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {p.ingredients?.length > 0 ? (
                            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                              {p.ingredients.length} ing.
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => setModal(p)}
                            className="text-blue-500 hover:text-blue-700 text-xs mr-3">Edit</button>
                          <button onClick={() => handleDelete(p)} disabled={deleting === p._id}
                            className="text-red-400 hover:text-red-600 text-xs disabled:opacity-40">
                            {deleting === p._id ? '…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {modal && (
        <ProductModal
          product={modal === 'add' ? null : modal}
          allProducts={products}
          categories={categories}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </Layout>
  );
}
